import { useCallback, useRef, useState } from 'react';

import { TOOTH_SLICE_LOOKAHEAD_PX } from '../config';

export const useIsInViewport = ({
  onChange,
  root = null,
  rootMarginBottomPx = TOOTH_SLICE_LOOKAHEAD_PX,
  rootMarginTopPx = TOOTH_SLICE_LOOKAHEAD_PX,
}: {
  root?: HTMLElement | null;
  rootMarginBottomPx?: number;
  rootMarginTopPx?: number;
  onChange?: (isVisible: boolean) => void;
} = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  const observerRef = useRef<IntersectionObserver | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const setTargetNode = useCallback(
    (node: HTMLElement | null) => {
      observerRef.current?.disconnect();
      observerRef.current = null;

      if (!node) {
        onChangeRef.current?.(false);
        setIsVisible(false);

        return;
      }

      const observer = new IntersectionObserver(
        ([entry]) => {
          onChangeRef.current?.(entry.isIntersecting);
          setIsVisible(entry.isIntersecting);
        },
        {
          root,
          rootMargin: `${rootMarginTopPx}px 0px ${rootMarginBottomPx}px 0px`,
        },
      );

      observer.observe(node);
      observerRef.current = observer;
    },
    [root, rootMarginBottomPx, rootMarginTopPx],
  );

  return { isVisible, setTargetNode };
};
